// Shared handling for the will upload identity check (OMW-436).
//
// The backend runs the check before it stores anything. When the uploaded document does
// not appear to belong to the testator it is being filed against, the mutation fails with
// a WILL_IDENTITY_MISMATCH error instead of saving. This mixin turns that error into a
// confirmation dialog and replays the same mutation with acknowledgedMismatch: true once
// the user confirms.
//
// The backend caches the check result against a digest of the file, so confirming does
// not pay for the OCR a second time.
export const MISMATCH_CODE = 'WILL_IDENTITY_MISMATCH';

export const willIdentityMismatchMixin = {
  data() {
    return {
      identityMismatchDialog: false,
      identityMismatchMessage: '',
      identityMismatchPrompt: '',
      identityMismatchConfirming: false,
      pendingUploadFileBase64: null,
    };
  },
  methods: {
    // Apollo wraps errors, so the extensions can arrive in a couple of shapes.
    identityMismatchFrom(error) {
      const errors =
        error?.graphQLErrors || error?.networkError?.result?.errors || [];

      return errors.find(
        graphQLError => graphQLError?.extensions?.code === MISMATCH_CODE
      );
    },

    // Returns true when the error was an identity mismatch and the dialog has been
    // raised, so callers can skip their normal error handling.
    handleIdentityMismatch(error, fileBase64) {
      const mismatch = this.identityMismatchFrom(error);
      if (!mismatch) return false;

      this.pendingUploadFileBase64 = fileBase64;
      this.identityMismatchMessage = mismatch.message;
      this.identityMismatchPrompt =
        mismatch.extensions.confirmationPrompt ||
        'Are you sure you want to upload this document?';
      this.identityMismatchConfirming = false;
      this.identityMismatchDialog = true;

      return true;
    },

    confirmIdentityMismatch() {
      this.identityMismatchConfirming = true;
      const fileBase64 = this.pendingUploadFileBase64;
      this.identityMismatchDialog = false;

      // Implemented by the component: re-runs its own upload mutation.
      this.performUpload(fileBase64, true);
    },

    cancelIdentityMismatch() {
      this.identityMismatchDialog = false;
      this.identityMismatchConfirming = false;
      this.pendingUploadFileBase64 = null;
      this.resetUploadInput();
    },

    // Overridable: components that hold a v-file-input clear it so the user can pick
    // another file straight away.
    resetUploadInput() {
      if ('file' in this) this.file = null;
      if ('buttonLoading' in this) this.buttonLoading = false;
    },

    // An upload that fails for a reason the component does not recognise used to
    // `throw`, which skipped the lines that clear the file input and the loading flag.
    // The form was then wedged: the spinner kept running and picking another file did
    // nothing, so a single server error looked like a dead page. Surface it and let the
    // user try again, while still logging the original for whoever is debugging.
    reportUnexpectedUploadError(error) {
      // eslint-disable-next-line no-console
      console.error('Will upload failed:', error);
      this.errorMessage =
        'Something went wrong uploading your will. Please try again, or contact support if it keeps happening.';
      this.displayError();
      this.resetUploadInput();
    },
  },
};
